
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useAuthMethods = () => {
  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (!error) {
        toast.success("Successfully signed in!");
      }
      
      return { error };
    } catch (error) {
      console.error('SignIn error:', error);
      return { error };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });
      
      if (!error) {
        toast.success("Account created! Please check your email to confirm your account.");
      }
      
      return { error };
    } catch (error) {
      console.error('SignUp error:', error);
      return { error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      
      return { error };
    } catch (error) {
      console.error('Google SignIn error:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      console.log('Starting logout process...');
      
      // Clear localStorage auth data first as a safety measure
      const authKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('sb-') || key.includes('supabase')
      );
      
      console.log('Found auth keys in localStorage:', authKeys);
      
      // Attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Supabase signOut error:', error);
        
        // If it's a "session not found" error, we still want to clear local state
        if (error.message?.includes('Session not found') || error.message?.includes('session')) {
          console.log('Session not found on server, clearing local state anyway');
          
          // Force clear all auth-related localStorage items
          authKeys.forEach(key => {
            localStorage.removeItem(key);
            console.log('Removed localStorage key:', key);
          });
          
          // Show success message since we've cleared local state
          toast.success("Successfully signed out.");
          
          return; // Don't throw error for session not found
        }
        
        throw error;
      }
      
      // If successful, also clear any remaining auth data
      authKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      console.log('Logout successful');
      toast.success("Successfully signed out.");
    } catch (error) {
      console.error('SignOut error:', error);
      
      // As a last resort, clear localStorage and show a warning
      const authKeys = Object.keys(localStorage).filter(key => 
        key.startsWith('sb-') || key.includes('supabase')
      );
      
      authKeys.forEach(key => {
        localStorage.removeItem(key);
      });
      
      toast.success("Signed out (cleared local session).");
      
      throw error;
    }
  };

  const forceSignOut = () => {
    console.log('Force signing out - clearing all local auth data');
    
    // Clear all auth-related localStorage items
    const authKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('sb-') || key.includes('supabase')
    );
    
    authKeys.forEach(key => {
      localStorage.removeItem(key);
      console.log('Force removed localStorage key:', key);
    });
    
    // Reload the page to reset the app state
    window.location.reload();
  };

  const deleteAccount = async (password: string) => {
    try {
      console.log('Starting account deletion...');
      
      // Get the current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('No active session found');
      }

      // Call the delete account Edge Function
      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: { password },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Account deletion error:', error);
        throw error;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      console.log('Account deletion successful');
      return { success: true };
    } catch (error) {
      console.error('Delete account error:', error);
      return { error };
    }
  };

  return {
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    forceSignOut,
    deleteAccount
  };
};
